import { useState, useMemo, useEffect } from 'react';
import { RevoltModal, RevoltButton } from '../../../components';
import { LoadingPanel } from '../../../components';
import './FilePickerModal.css';

export function FilePickerModal({
  isOpen,
  onClose,
  appId,
  onSelectFile
}) {
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState(new Set(['application']));
  const [selectedFile, setSelectedFile] = useState(null);

  // Fetch files when modal opens
  useEffect(() => {
    if (isOpen && appId) {
      setLoadingFiles(true);
      fetch(`/api/apps/${appId}/files`, {
        credentials: 'include'
      })
        .then(res => res.json())
        .then(data => {
          setFiles(data.files || []);
          setLoadingFiles(false);
        })
        .catch(err => {
          console.error('Failed to fetch app files:', err);
          setFiles([]);
          setLoadingFiles(false);
        });
    } else if (!isOpen) {
      // Reset state when closing
      setSelectedFile(null);
    }
  }, [isOpen, appId]);

  // Check if file is HTML
  const isHtmlFile = (path) => {
    const ext = path.split('.').pop().toLowerCase();
    return ext === 'html' || ext === 'htm';
  };

  // Build filtered tree structure from flat file list
  const fileTree = useMemo(() => {
    if (!files || files.length === 0) return [];

    const tree = [];
    const pathMap = new Map(); // path -> node

    // 1. Filter out unwanted folders and non-HTML files
    const filteredFiles = files.filter(file => {
      const parts = file.path.split('/');
      // Exclude .git and .revolt anywhere in the path
      if (parts.some(p => p === '.git' || p === '.revolt')) return false;
      // Only keep HTML files
      return isHtmlFile(file.path);
    });

    if (filteredFiles.length === 0) return [];

    // 2. Sort files by path for consistent ordering
    const sortedFiles = [...filteredFiles].sort((a, b) => a.path.localeCompare(b.path));

    for (const file of sortedFiles) {
      const pathParts = file.path.split('/').filter(Boolean);
      let currentLevel = tree;
      let currentPath = '';

      for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i];
        const isLast = i === pathParts.length - 1;
        const path = currentPath ? `${currentPath}/${part}` : part;

        if (isLast) {
          // This is the file
          const fileNode = {
            id: `file:${path}`,
            name: part,
            path: path,
            type: 'file',
            extension: part.includes('.') ? part.split('.').pop().toLowerCase() : ''
          };
          currentLevel.push(fileNode);
          pathMap.set(path, fileNode);
        } else {
          // This is a folder
          let folderNode = pathMap.get(path);
          if (!folderNode) {
            folderNode = {
              id: `folder:${path}`,
              name: part,
              path: path,
              type: 'folder',
              children: []
            };
            currentLevel.push(folderNode);
            pathMap.set(path, folderNode);
          }
          currentLevel = folderNode.children;
          currentPath = path;
        }
      }
    }

    return tree;
  }, [files]);

  const toggleFolder = (folderPath) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderPath)) {
      newExpanded.delete(folderPath);
    } else {
      newExpanded.add(folderPath);
    }
    setExpandedFolders(newExpanded);
  };

  const handleFileClick = (file) => {
    setSelectedFile(file);
  };

  const handleConfirm = () => {
    if (selectedFile) {
      // Ensure path starts with a leading slash
      const path = selectedFile.path.startsWith('/') 
        ? selectedFile.path 
        : `/${selectedFile.path}`;
      onSelectFile(path);
    }
  };

  const renderFileTree = (items, level = 0) => {
    return items.map((item) => {
      const isExpanded = expandedFolders.has(item.path);
      const isSelected = selectedFile?.id === item.id;
      const indent = level * 16;

      if (item.type === 'folder') {
        return (
          <div key={item.id}>
            <div
              className={`file-picker-item folder ${isExpanded ? 'expanded' : ''}`}
              style={{ paddingLeft: `${16 + indent}px` }}
              onClick={() => toggleFolder(item.path)}
            >
              <span className="file-picker-arrow">
                <i className={`fa-solid fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
              </span>
              <span className="file-picker-icon">
                <i className={`fa-solid fa-folder${isExpanded ? '-open' : ''}`}></i>
              </span>
              <span className="file-picker-name">{item.name}</span>
            </div>
            {isExpanded && item.children && (
              <div className="file-picker-children">
                {renderFileTree(item.children, level + 1)}
              </div>
            )}
          </div>
        );
      } else {
        return (
          <div
            key={item.id}
            className={`file-picker-item file ${isSelected ? 'selected' : ''}`}
            style={{ paddingLeft: `${16 + indent + 16}px` }}
            onClick={() => handleFileClick(item)}
          >
            <span className="file-picker-icon">🌐</span>
            <span className="file-picker-name">{item.name}</span>
          </div>
        );
      }
    });
  };

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Entry File"
      containerClassName="file-picker-modal"
    >
      <div className="file-picker-content">
        <div className="file-picker-header-desc">
          <p>Please navigate through the project structure and select the HTML file that should serve as the entry point for your application.</p>
        </div>

        <div className="file-picker-main">
          {loadingFiles ? (
            <div className="file-picker-loading">
              <LoadingPanel
                title="Loading Files"
                subtitle="Fetching app files..."
                showAppName={false}
              />
            </div>
          ) : fileTree.length === 0 ? (
            <div className="file-picker-empty">
              <i className="fa-solid fa-file-circle-xmark"></i>
              <p>No HTML files found in this app.</p>
              <small>Only HTML files can be set as entry points.</small>
            </div>
          ) : (
            <div className="file-picker-tree">
              <div className="file-picker-tree-content">
                {renderFileTree(fileTree)}
              </div>
            </div>
          )}
        </div>

        <div className="file-picker-selection">
          <div className="file-picker-selection-label">Selected Path:</div>
          <div className={`file-picker-selection-path ${selectedFile ? 'active' : ''}`}>
            {selectedFile ? `/${selectedFile.path}` : 'No file selected'}
          </div>
        </div>

        <div className="file-picker-actions">
          <RevoltButton variant="accent" onClick={onClose}>
            Cancel
          </RevoltButton>
          <RevoltButton
            variant="outline"
            onClick={handleConfirm}
            disabled={!selectedFile}
          >
            Confirm
          </RevoltButton>
        </div>
      </div>
    </RevoltModal>
  );
}
